import moment from 'moment-timezone/builds/moment-timezone-with-data.min';
// @ts-expect-error: локаль moment не имеет типовых определений
import 'moment/locale/ru';

moment.locale('ru');

export function convertToTimezoneMoment(isoDateTime: string, format = 'YYYY-MM-DD') {
    const raw = localStorage.getItem('user_timeZone');
    const timezone = raw?.replace(/^"+|"+$/g, '') || 'Europe/Moscow';

    if (!moment.tz.zone(timezone)) {
        console.warn(`Invalid timezone: "${timezone}". Falling back to Europe/Moscow.`);
        return moment.utc(isoDateTime).tz('Europe/Moscow').format(format);
    }

    return moment.utc(isoDateTime).format(format);
}

export function convertToTimezoneTimeOnly(timeStr: string): string {
    const raw = localStorage.getItem('user_timeZone');
    const timezone = raw?.replace(/^"+|"+$/g, '') || 'Europe/Moscow';

    const isoDateTime = `1970-01-01T${timeStr.replace('Z', '')}Z`; // valid ISO 8601

    if (!moment.tz.zone(timezone)) {
        console.warn(`Invalid timezone: "${timezone}". Falling back to Europe/Moscow.`);
        return moment.utc(isoDateTime).tz('Europe/Moscow').format('HH:mm:ss');
    }

    return moment.utc(isoDateTime).tz(timezone).format('HH:mm:ss');
}

export function convertOffsetTimeToLocalHHmm(timeStr: string): string {
    const raw = localStorage.getItem('user_timeZone');
    const timezone = raw?.replace(/^"+|"+$/g, '') || 'Europe/Moscow';

    if (!moment.tz.zone(timezone)) {
        console.warn(`Invalid timezone: "${timezone}". Falling back to Europe/Moscow.`);
        return moment.parseZone(timeStr).tz('Europe/Moscow').format('HH:mm');
    }

    return moment.parseZone(timeStr).tz(timezone).format('HH:mm');
}

export function convertUtcTimeOnlyToLocalHHmm(utcTimeWithOffset: string): string {
    const raw = localStorage.getItem('user_timeZone');
    const timezone = raw?.replace(/^"+|"+$/g, '') || 'Europe/Moscow';

    if (!moment.tz.zone(timezone)) {
        console.warn(`Invalid timezone: "${timezone}". Falling back to UTC.`);
        return moment.utc().format('HH:mm');
    }

    // utcTimeWithOffset: "14:00:34.866000+00:00"
    // Добавим сегодняшнюю дату, чтобы получить корректный ISO с датой + временем
    // Отрежем лишние микросекунды (6 цифр) до 3 микросекунд или просто оставим 3
    const sanitizedTime = utcTimeWithOffset.replace(/(\.\d{3})\d+/, '$1');

    // Сегодняшняя дата в формате YYYY-MM-DD
    const todayDate = moment().format('YYYY-MM-DD');

    // Собираем полный ISO с датой и временем
    const fullIsoString = `${todayDate}T${sanitizedTime}`;

    // Парсим с учетом смещения и конвертируем в нужный часовой пояс
    return moment.parseZone(fullIsoString).tz(timezone).format('HH:mm');
}

export const formatDateForApi = (publication_date: string | Date) => {
    const formattedDate = moment(publication_date).format('YYYY-MM-DD');
    return formattedDate;
}

export function formatTimeForApi(time: string): string {
    const [hours, minutes] = time.split(':').map(Number);

    const raw = localStorage.getItem('user_timeZone');
    const timezone = raw?.replace(/^"+|"+$/g, '') || 'Europe/Moscow';

    if (!moment.tz.zone(timezone)) {
        console.warn(`Invalid timezone: "${timezone}". Using UTC.`);
        return moment.utc({
            year: 1970,
            month: 0,
            day: 1,
            hour: hours,
            minute: minutes,
            second: 0,
            millisecond: 0,
        }).toISOString().slice(11); // "HH:mm:ss.SSSZ"
    }

    // Используем текущую дату, чтобы учесть DST на сегодня
    const now = moment();
    const dateWithTime = moment.tz(
        {
            year: now.year(),
            month: now.month(),
            day: now.date(),
            hour: hours,
            minute: minutes,
            second: 0,
            millisecond: 0,
        },
        timezone
    );

    return dateWithTime.utc().toISOString().slice(11); // → "HH:mm:ss.SSSZ"
}

export function formatTimeFromApi(utcTime: string): string {
    const raw = localStorage.getItem('user_timeZone');
    const timezone = raw?.replace(/^"+|"+$/g, '') || 'Europe/Moscow';

    if (!moment.tz.zone(timezone)) {
        console.warn(`Invalid timezone: "${timezone}". Using UTC.`);
        return moment.utc(`1970-01-01T${utcTime}`).format('HH:mm');
    }

    const today = moment();
    const year = today.year(); // <-- это важно
    const month = String(today.month() + 1).padStart(2, '0');
    const day = String(today.date()).padStart(2, '0');

    const isoDateTime = `${year}-${month}-${day}T${utcTime}`;
    const utcFullDateTime = moment.utc(isoDateTime);

    const localTime = utcFullDateTime.clone().tz(timezone);
    return localTime.format('HH:mm');
}

export function formatScheduledAt(scheduledAtUtc: string): string {
    const raw = localStorage.getItem('user_timeZone');
    const timezone = raw?.replace(/^"+|"+$/g, '') || 'Europe/Moscow';

    const date = new Date(scheduledAtUtc);

    const formatter = new Intl.DateTimeFormat('ru-RU', {
        timeZone: timezone,
        hour: '2-digit',
        minute: '2-digit',
        second: '2-digit',
        day: 'numeric',
        month: 'long',
        year: 'numeric',
    });

    // Пример: "15:16:00 20 мая 2025"
    const parts = formatter.formatToParts(date);

    const time = `${parts.find(p => p.type === 'hour')?.value}:${parts.find(p => p.type === 'minute')?.value}:${parts.find(p => p.type === 'second')?.value}`;
    const day = parts.find(p => p.type === 'day')?.value;
    const month = parts.find(p => p.type === 'month')?.value;
    const year = parts.find(p => p.type === 'year')?.value;

    return `${time} ${day} ${month} ${year}`;
}

export function formatDayMonthFromApi(utcDateTime: string): string {
    const raw = localStorage.getItem('user_timeZone');
    const timezone = raw?.replace(/^"+|"+$/g, '') || 'Europe/Moscow';

    if (!moment.tz.zone(timezone)) {
        console.warn(`Invalid timezone: "${timezone}". Using UTC.`);
        return moment.utc(utcDateTime).format('DD.MM');
    }

    return moment.utc(utcDateTime).tz(timezone).format('DD.MM');
}

export function formatScheduledAtHHMMDDDD(scheduledAtUtc: string): string {
    const raw = localStorage.getItem('user_timeZone');
    const timezone = raw?.replace(/^"+|"+$/g, '') || 'Europe/Moscow';

    const date = new Date(scheduledAtUtc);

    const formatter = new Intl.DateTimeFormat('ru-RU', {
        timeZone: timezone,
        hour: '2-digit',
        minute: '2-digit',
        second: '2-digit',
        day: 'numeric',
        month: 'long',
        year: 'numeric',
    });

    // Пример: "15:16:00 20 мая 2025"
    const parts = formatter.formatToParts(date);

    const time = `${parts.find(p => p.type === 'hour')?.value}:${parts.find(p => p.type === 'minute')?.value}`;
    const day = parts.find(p => p.type === 'day')?.value;
    const month = parts.find(p => p.type === 'month')?.value;
    const year = parts.find(p => p.type === 'year')?.value;

    return `${time} ${day} ${month} ${year}`;
}

export const timezones = [
    {value: 'America/Los_Angeles', title: 'America/Los_Angeles (UTC-07:00)', index: 1},
    {value: 'America/Denver', title: 'America/Denver (UTC-06:00)', index: 2},
    {value: 'America/Chicago', title: 'America/Chicago (UTC-05:00)', index: 3},
    {value: 'America/New_York', title: 'America/New_York (UTC-04:00)', index: 4},
    {value: 'America/Sao_Paulo', title: 'America/Sao_Paulo (UTC-03:00)', index: 5},
    {value: 'Atlantic/Azores', title: 'Atlantic/Azores (UTC-01:00)', index: 6},
    {value: 'UTC', title: 'UTC (UTC±00:00)', index: 7},
    {value: 'Europe/London', title: 'Europe/London (UTC+01:00)', index: 8},
    {value: 'Europe/Berlin', title: 'Europe/Berlin (UTC+02:00)', index: 9},
    {value: 'Europe/Moscow', title: 'Europe/Moscow (UTC+03:00)', index: 10},
    {value: 'Asia/Dubai', title: 'Asia/Dubai (UTC+04:00)', index: 11},
    {value: 'Asia/Karachi', title: 'Asia/Karachi (UTC+05:00)', index: 12},
    {value: 'Asia/Dhaka', title: 'Asia/Dhaka (UTC+06:00)', index: 13},
    {value: 'Asia/Bangkok', title: 'Asia/Bangkok (UTC+07:00)', index: 14},
    {value: 'Asia/Shanghai', title: 'Asia/Shanghai (UTC+08:00)', index: 15},
    {value: 'Asia/Tokyo', title: 'Asia/Tokyo (UTC+09:00)', index: 16},
    {value: 'Australia/Sydney', title: 'Australia/Sydney (UTC+10:00)', index: 17},
    {value: 'Pacific/Noumea', title: 'Pacific/Noumea (UTC+11:00)', index: 18},
    {value: 'Pacific/Auckland', title: 'Pacific/Auckland (UTC+12:00)', index: 19},
    {value: 'Africa/Cairo', title: 'Africa/Cairo (UTC+02:00)', index: 20},
    {value: 'Asia/Seoul', title: 'Asia/Seoul (UTC+09:00)', index: 21},
    {value: 'Asia/Singapore', title: 'Asia/Singapore (UTC+08:00)', index: 22},
    {value: 'Asia/Jakarta', title: 'Asia/Jakarta (UTC+07:00)', index: 23},
    {value: 'Asia/Tehran', title: 'Asia/Tehran (UTC+03:30)', index: 24},
    {value: 'Asia/Kolkata', title: 'Asia/Kolkata (UTC+05:30)', index: 25},
    {value: 'Asia/Yekaterinburg', title: 'Asia/Yekaterinburg (UTC+05:00)', index: 26},
    {value: 'America/Toronto', title: 'America/Toronto (UTC-04:00)', index: 27},
    {value: 'Europe/Istanbul', title: 'Europe/Istanbul (UTC+03:00)', index: 28},
    {value: 'Europe/Paris', title: 'Europe/Paris (UTC+02:00)', index: 29},
];
export const languages = [
    {value: 'en', title: 'English / Английский', index: 1},
    {value: 'zh', title: 'Chinese / Китайский', index: 2},
    {value: 'hi', title: 'Hindi / Хинди', index: 3},
    {value: 'es', title: 'Spanish / Испанский', index: 4},
    {value: 'fr', title: 'French / Французский', index: 5},
    {value: 'ar', title: 'Arabic / Арабский', index: 6},
    {value: 'bn', title: 'Bengali / Бенгальский', index: 7},
    {value: 'ru', title: 'Russian / Русский', index: 8},
    {value: 'pt', title: 'Portuguese / Португальский', index: 9},
    {value: 'ur', title: 'Urdu / Урду', index: 10},
    {value: 'id', title: 'Indonesian / Индонезийский', index: 11},
    {value: 'de', title: 'German / Немецкий', index: 12},
    {value: 'ja', title: 'Japanese / Японский', index: 13},
    {value: 'sw', title: 'Swahili / Суахили', index: 14},
    {value: 'mr', title: 'Marathi / Маратхи', index: 15},
    {value: 'te', title: 'Telugu / Телугу', index: 16},
    {value: 'tr', title: 'Turkish / Турецкий', index: 17},
    {value: 'ta', title: 'Tamil / Тамильский', index: 18},
    {value: 'vi', title: 'Vietnamese / Вьетнамский', index: 19},
    {value: 'ko', title: 'Korean / Корейский', index: 20},
    {value: 'fa', title: 'Persian / Персидский', index: 21},
    {value: 'pl', title: 'Polish / Польский', index: 22},
    {value: 'uk', title: 'Ukrainian / Украинский', index: 23},
    {value: 'ro', title: 'Romanian / Румынский', index: 24},
    {value: 'nl', title: 'Dutch / Голландский', index: 25},
    {value: 'hu', title: 'Hungarian / Венгерский', index: 26},
    {value: 'el', title: 'Greek / Греческий', index: 27},
    {value: 'sv', title: 'Swedish / Шведский', index: 28},
    {value: 'he', title: 'Hebrew / Иврит', index: 29},
    {value: 'cs', title: 'Czech / Чешский', index: 30},
];

export function getNearestTimezoneAndSave() {
    const userTimezone = Intl.DateTimeFormat().resolvedOptions().timeZone;

    const exactMatch = timezones.find(tz => tz.value === userTimezone);
    const now = new Date();
    const userOffsetMinutes = now.getTimezoneOffset();
    const userOffsetHours = -userOffsetMinutes / 60;

    function parseUtcOffset(title: string): number | null {
        const match = title.match(/UTC([+-])(\d{2}):(\d{2})/);
        if (!match) return null;
        const sign = match[1] === '+' ? 1 : -1;
        const hours = parseInt(match[2], 10);
        const minutes = parseInt(match[3], 10);
        return sign * (hours + minutes / 60);
    }

    let matchedTz = exactMatch;
    if (!matchedTz) {
        let closest = timezones[0];
        let minDiff = Infinity;
        for (const tz of timezones) {
            const offset = parseUtcOffset(tz.title);
            if (offset !== null) {
                const diff = Math.abs(offset - userOffsetHours);
                if (diff < minDiff) {
                    minDiff = diff;
                    closest = tz;
                }
            }
        }
        matchedTz = closest;
    }

    // Сохраняем значение в localStorage
    if (matchedTz) {
        localStorage.setItem("user_timeZone", JSON.stringify(matchedTz.value));
    }
    console.log(matchedTz)
    return matchedTz;
}



